import { currencyList } from 'src/model/payment'

// Constants
export const SizeType = {
  SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
  ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
  TILE: { name: 'Tile', class: 'contain' },
}
export const getPaymentFrequency = price => {
  if (price.priceType === 1) {
    return {
      value: 'oneTime',
      label: '',
    }
  }

  if (price.priceType === 2) {
    const recurring = price.priceMetadata?.recurring

    if (!recurring) {
      return { value: 'oneTime', label: '' }
    }

    const { interval, interval_count } = recurring

    if (interval === 'week') {
      return { value: 'week', label: '/Week' }
    }

    if (interval === 'month') {
      if (interval_count === 1) {
        return { value: 'month', label: '/Month' }
      }
      if (interval_count === 3) {
        return { value: 'every3Month', label: '/Every 3 months' }
      }
      if (interval_count === 6) {
        return { value: 'every6Months', label: '/Every 6 months' }
      }
    }

    if (interval === 'year') {
      return { value: 'year', label: '/Year' }
    }
  }

  return { value: 'oneTime', label: '' }
}

export const formatPrice = price => {
  if (!price || !price.amount) return 'Free'

  const amount = parseFloat(price.amount)
  const currencyCode = price.currency || 'USD'

  const currencyInfo = currencyList.find(c => c.code === currencyCode)

  if (!currencyInfo) {
    return `${currencyCode} ${amount}`
  }

  const symbol = currencyInfo.symbol
  const position = currencyInfo.symbolPosition

  if (position === 'right') {
    return `${amount}${symbol}`
  } else {
    return `${symbol}${amount}`
  }
}
export const hexToRGBA = (hex, opacity) => {
  try {
    hex = hex.replace('#', '')

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('')
    }
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    const validOpacity = Math.min(Math.max(opacity, 0), 1)

    return `rgba(${r}, ${g}, ${b}, ${validOpacity})`
  } catch (error) {
    console.error('Error converting hex to RGBA:', error)
    return 'rgba(0, 0, 0, 1)'
  }
}

// Editor update utilities
export const updateModelAttribute = (
  editor,
  selection,
  attributeName,
  value,
) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (modelElement.getAttribute(attributeName) !== value) {
        writer.setAttribute(attributeName, value, modelElement)
      }
    }
  })
}

export const updateViewStyle = (editor, selection, styleName, value) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(styleName, value, viewElement)
        })
      }
    }
  })
}

export const handleButtonWidth = (editor, selection, newSizing) => {
  if (!selection || newSizing === null) return

  const buttonWrapper = selection.getChild(0).getChild(0).getChild(0)
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        if (modelElement.getAttribute('dataSizing') !== newSizing) {
          writer.setAttribute('dataSizing', newSizing, modelElement)
        }
      }
    })
  }
}

export const handleContainerWidth = (editor, selection, newSizing) => {
  if (!selection || newSizing === null) return

  const buttonWrapper = selection
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        if (modelElement.getAttribute('dataSizing') !== newSizing) {
          writer.setAttribute('dataSizing', newSizing, modelElement)
        }
      }
    })
  }
}

// Handler functions
export const handleAlignment = (editor, selection, newAlignment) => {
  if (newAlignment === null) return

  const buttonWrapper = selection.getChild(0).getChild(0).getChild(0)
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        if (modelElement.getAttribute('dataAlignment') !== newAlignment) {
          writer.setAttribute('dataAlignment', newAlignment, modelElement)
        }
      }
    })
  }
}

export const handleBackground = (editor, selection, newBackground) => {
  if (newBackground === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (newBackground === 'color') {
        if (modelElement.getAttribute('backgroundImage') !== '') {
          writer.setAttribute('backgroundImage', '', modelElement)
        }
      } else {
        if (modelElement.getAttribute('backgroundColor') !== '') {
          writer.setAttribute('backgroundColor', '', modelElement)
        }
      }

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          if (newBackground === 'color') {
            writer.setStyle('background-image', '', viewElement)
          } else {
            writer.setStyle('background-color', '', viewElement)
          }
        })
      }
    }
  })

  updateModelAttribute(editor, selection, 'backgroundType', newBackground)
}

export const handleSize = (editor, selection, selectedSize) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (
        modelElement.getAttribute('backgroundSize') !==
        SizeType[selectedSize].class
      ) {
        writer.setAttribute(
          'backgroundSize',
          SizeType[selectedSize].class,
          modelElement,
        )
      }
      if (modelElement.getAttribute('sizeType') !== selectedSize) {
        writer.setAttribute('sizeType', selectedSize, modelElement)
      }

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(
            'background-size',
            SizeType[selectedSize].class,
            viewElement,
          )
          if (selectedSize === 'ORIGINAL_SIZE') {
            writer.setStyle('background-repeat', 'no-repeat', viewElement)
          } else {
            writer.removeStyle('background-repeat', viewElement)
          }
        })
      }
    }
  })
}

export const handleImageDelete = (editor, selection) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (modelElement.getAttribute('backgroundImage') !== '') {
        writer.setAttribute('backgroundImage', '', modelElement)
      }
      if (modelElement.getAttribute('backgroundColor') !== '') {
        writer.setAttribute('backgroundColor', '', modelElement)
      }
      if (modelElement.getAttribute('backgroundSize') !== '') {
        writer.setAttribute('backgroundSize', '', modelElement)
      }
      if (modelElement.getAttribute('sizeType') !== 'SCALE_TO_FILL') {
        writer.setAttribute('sizeType', 'SCALE_TO_FILL', modelElement)
      }
      if (modelElement.getAttribute('backgroundPosition') !== 'center') {
        writer.setAttribute('backgroundPosition', 'center', modelElement)
      }

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-image', '', viewElement)
        })
      }
    }
  })
}

export const handleOverlayColor = (
  editor,
  selection,
  color,
  opacityValue,
  imageURL,
  isOverlayEnabled = true,
) => {
  if (!selection || color === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      const rgbaColor = hexToRGBA(color, opacityValue)
      writer.setAttribute('overlayColor', color, modelElement)
      writer.setAttribute('linearValue', rgbaColor, modelElement)
      writer.setAttribute('opacity', opacityValue, modelElement)
      // The data downcast only writes the gradient when this flag is 'true',
      // so keep it in sync here as well - otherwise the overlay shows up in
      // the editor but is missing from the saved HTML (and from the app).
      writer.setAttribute(
        'useImageOverlay',
        String(!!isOverlayEnabled),
        modelElement,
      )

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(
            'background-image',
            isOverlayEnabled
              ? `linear-gradient(${rgbaColor},${rgbaColor}) ,url(${imageURL})`
              : `url(${imageURL})`,
            viewElement,
          )
        })
      }
    }
  })
}

export const handlePosition = (editor, selection, position) => {
  if (!selection || position === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (modelElement.getAttribute('backgroundPosition') !== position) {
        writer.setAttribute('backgroundPosition', position, modelElement)
      }
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-position', position, viewElement)
        })
      }
    }
  })
}

export const handleColor = (editor, selection, color) => {
  if (!selection || color === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('backgroundColor', color, modelElement)

      // View tarafını da güncelle
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-color', color, viewElement)
        })
      }
    }
  })
}

export const handleImageOverlay = (
  editor,
  selection,
  isChecked,
  imageURL = '',
) => {
  if (!selection) return

  // `isChecked` is the value *before* the toggle, so the new state is !isChecked
  const isEnabled = !isChecked

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (modelElement.getAttribute('useImageOverlay') !== String(isEnabled)) {
        writer.setAttribute('useImageOverlay', String(isEnabled), modelElement)
      }

      // Keep the editing view in sync so the editor matches the saved HTML.
      const rgbaColor = modelElement.getAttribute('linearValue')
      const backgroundImage =
        imageURL || modelElement.getAttribute('backgroundImage') || ''
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement && backgroundImage) {
        editor.editing.view.change(viewWriter => {
          viewWriter.setStyle(
            'background-image',
            isEnabled && rgbaColor
              ? `linear-gradient(${rgbaColor},${rgbaColor}) ,url(${backgroundImage})`
              : `url(${backgroundImage})`,
            viewElement,
          )
        })
      }
    }
  })
}

export const handleContainerType = (editor, selection, containerType) => {
  if (!selection || containerType === null) return

  const ContainerTypeWidths = {
    dar: '600px',
    orta: '800px',
    genis: '1200px',
  }

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('containerType', containerType, modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setAttribute('container-type', containerType, viewElement)

          if (containerType && ContainerTypeWidths[containerType]) {
            writer.setStyle('max-width', ContainerTypeWidths[containerType], viewElement)
            writer.setStyle('margin-left', 'auto', viewElement)
            writer.setStyle('margin-right', 'auto', viewElement)
          } else {
            writer.removeStyle('max-width', viewElement)
            writer.removeStyle('margin-left', viewElement)
            writer.removeStyle('margin-right', viewElement)
          }
        })
      }
    }
  })
}
